import React, { useEffect, useRef, useState } from 'react';
import { Globe, Check } from 'lucide-react';
import { useLanguage, SUPPORTED_LANGUAGES } from '../contexts/LanguageContext';

/**
 * Mobile-only floating language pill — bottom-LEFT counterpart to the
 * bottom-right Donate Now floater. Hidden on `lg:` and up because the public
 * header already shows a globe dropdown on desktop.
 *
 * Tap the pill → a small popover slides up with the four supported languages
 * (EN / ES / HT / FR) shown in their native names + flags. Selecting one
 * translates the entire site immediately.
 *
 * Props:
 *  - lifted: boolean — match the Donate floater's `lifted` offset when a
 *    sticky checkout bar (e.g. pop-up shop cart bar) is present.
 */
const FloatingLanguageTab = ({ lifted = false }) => {
  const { lang, setLang } = useLanguage();
  const [open, setOpen] = useState(false);
  const wrapperRef = useRef(null);

  useEffect(() => {
    if (!open) return undefined;
    const handler = (e) => {
      if (wrapperRef.current && !wrapperRef.current.contains(e.target)) setOpen(false);
    };
    document.addEventListener('mousedown', handler);
    document.addEventListener('touchstart', handler);
    return () => {
      document.removeEventListener('mousedown', handler);
      document.removeEventListener('touchstart', handler);
    };
  }, [open]);

  const current = SUPPORTED_LANGUAGES.find((l) => l.code === lang) || SUPPORTED_LANGUAGES[0];

  const bottomClass = lifted
    ? 'bottom-[calc(260px+env(safe-area-inset-bottom))] sm:bottom-[calc(220px+env(safe-area-inset-bottom))]'
    : 'bottom-[calc(0.75rem+env(safe-area-inset-bottom))]';

  return (
    <div
      ref={wrapperRef}
      className={`lg:hidden fixed ${bottomClass} left-4 sm:left-6 z-[99999]`}
      data-testid="floating-lang-tab"
      data-floating-tab="language"
    >
      {open && (
        <div
          className="absolute bottom-full left-0 mb-2 w-56 bg-white border border-[#E5E7EB] rounded-2xl shadow-[0_8px_28px_rgba(0,0,0,0.15)] py-1.5 overflow-hidden"
          role="listbox"
          data-testid="floating-lang-popover"
        >
          <div className="px-3 py-1.5 border-b border-[#F1F5F9]">
            <p className="text-[10px] uppercase tracking-wider font-bold text-[#9CA3AF] inline-flex items-center gap-1">
              <Globe className="w-3 h-3" /> Language
            </p>
          </div>
          {SUPPORTED_LANGUAGES.map((l) => (
            <button
              key={l.code}
              type="button"
              onClick={() => { setLang(l.code); setOpen(false); }}
              className={`w-full text-left px-3 py-2 text-sm flex items-center gap-2.5 active:bg-[#F8F9FA] ${
                lang === l.code ? 'bg-[#FFF8F8] text-[#BA1826] font-bold' : 'text-[#0A0A0A]'
              }`}
              data-testid={`floating-lang-option-${l.code}`}
            >
              <span className="text-lg leading-none">{l.flag}</span>
              <span className="flex-1">{l.native}</span>
              {lang === l.code && <Check className="w-4 h-4" />}
            </button>
          ))}
        </div>
      )}

      <button
        type="button"
        onClick={() => setOpen((o) => !o)}
        aria-haspopup="listbox"
        aria-expanded={open}
        aria-label="Change language"
        className="flex items-center gap-2 bg-[#0A0A0A] text-white pl-3 pr-4 py-2.5 rounded-full shadow-[0_4px_20px_rgba(10,10,10,0.35)] hover:bg-[#1F1F1F] transition-all duration-300 active:scale-95"
        data-testid="floating-lang-btn"
      >
        <Globe className="w-5 h-5 text-white flex-shrink-0" />
        <span className="text-base leading-none">{current.flag}</span>
        <span className="text-xs font-bold uppercase tracking-wider">{current.code}</span>
      </button>
    </div>
  );
};

export default FloatingLanguageTab;
